import React, { forwardRef } from 'react';
import { CANVAS_WIDTH, getCanvasHeight, type StudioPageData, type StudioSettings } from '../../constants';
import StudioAdvisorQr from './StudioAdvisorQr';
import { StudioTypeScaleProvider } from './StudioTypeScale';
import { getStudioLayout } from './layouts';
import { STUDIO_PAGE_NUMBER_RIGHT, STUDIO_PAGE_NUMBER_RIGHT_STORY } from './StudioPageNumber';

interface StudioPageProps {
  page: StudioPageData;
  settings: StudioSettings;
  pageNumber?: number;
}

const CHUBB_DISCLAIMERS: Record<string, string> = {
  'zh-Hant-HK': '此為PortfoPlus製作資料，\n內容僅供一般資訊參考及分享，不構成任何專業建議。',
  zh: '此为PortfoPlus制作资料，\n内容仅供一般信息参考及分享，不构成任何专业建议。',
  en: 'PortfoPlus material for general information only. Not professional advice.'
};

const StudioPage = forwardRef<HTMLDivElement, StudioPageProps>(({ page, settings, pageNumber }, ref) => {
  const Layout = getStudioLayout(page && page.layoutType, settings.template);
  const localeContent =
    (page && page.content && (page.content[settings.locale] || page.content['zh-Hant-HK'] || page.content.en)) || {};
  const disclaimer = CHUBB_DISCLAIMERS[settings.locale] || CHUBB_DISCLAIMERS['zh-Hant-HK'];
  const isLastPage =
    typeof pageNumber === 'number' &&
    typeof settings.pageCount === 'number' &&
    settings.pageCount > 0 &&
    pageNumber === settings.pageCount;
  const lastPageQrUrl = isLastPage ? settings.qrCodeUrl : undefined;
  const isCtaPage = Boolean(page && page.layoutType === 'cta');

  return (
    <StudioTypeScaleProvider locale={settings.locale}>
      <div ref={ref} style={{ width: CANVAS_WIDTH, height: getCanvasHeight(settings.format), position: 'relative' }}>
        <Layout
          width={CANVAS_WIDTH}
          height={getCanvasHeight(settings.format)}
          format={settings.format}
          locale={settings.locale}
          content={localeContent}
          themeColor={settings.themeColor || '#1456f0'}
          profileName={settings.profileName}
          watermarkLogo={isCtaPage ? null : settings.watermarkLogo}
          pageNumber={pageNumber}
          pageCount={settings.pageCount}
          showChubbDisclaimer={settings.showChubbDisclaimer}
          qrCodeUrl={isCtaPage ? lastPageQrUrl : undefined}
        />
        {lastPageQrUrl && !isCtaPage ? (
          <div
            style={{
              position: 'absolute',
              zIndex: 5,
              right: settings.format === 'story' ? STUDIO_PAGE_NUMBER_RIGHT_STORY : STUDIO_PAGE_NUMBER_RIGHT,
              bottom: settings.showChubbDisclaimer ? 42 : 10
            }}
          >
            <StudioAdvisorQr url={lastPageQrUrl} isStory={settings.format === 'story'} size="cta" />
          </div>
        ) : null}
        {settings.showChubbDisclaimer ? (
          <div
            style={{
              position: 'absolute',
              zIndex: 10,
              bottom: 8,
              left: 16,
              right: 16,
              padding: 0,
              boxSizing: 'border-box',
              color: '#9aa9c6',
              fontFamily: "'DM Sans', 'Noto Sans TC', 'Noto Sans SC', Arial, sans-serif",
              fontSize: 8,
              fontWeight: 600,
              lineHeight: 1.35,
              textAlign: 'center',
              whiteSpace: 'pre-line',
              textShadow: settings.template === 'spotlight' ? 'none' : '0 1px 2px rgba(255, 255, 255, 0.55)'
            }}
          >
            {disclaimer}
          </div>
        ) : null}
      </div>
    </StudioTypeScaleProvider>
  );
});

export default StudioPage;
